import React, { useState } from 'react';
import { PageHeader } from '@/components/PageHeader';
import { ModuleAccessGuard } from '@/components/ModuleAccessGuard';
import { FilterBar } from '@/components/shared/FilterBar';
import { DataTable } from '@/components/shared/DataTable';
import { useCollectionList } from '@/hooks/useBillsAPI';
import { useCustomers } from '@/hooks/useCustomers';
import { useSuppliers } from '@/hooks/useSuppliers';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Eye, FileText, Download } from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const ReviewParsedBills = () => {
  const [search, setSearch] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const { customers } = useCustomers();
  const { suppliers } = useSuppliers();

  const filters = {
    ...activeFilters,
    parsed: true,
    search,
    limit: pageSize,
    offset: (currentPage - 1) * pageSize,
  };

  const { data, isLoading } = useCollectionList(filters);

  const filterConfigs = [
    {
      key: 'customer_id',
      label: 'Customer',
      type: 'select' as const,
      options: customers?.map((c: any) => ({ value: c.id, label: c.name })) || [],
      placeholder: 'All customers',
    },
    {
      key: 'supplier_id',
      label: 'Supplier',
      type: 'select' as const,
      options: suppliers?.map((s: any) => ({ value: s.id, label: s.name })) || [],
      placeholder: 'All suppliers',
    },
    {
      key: 'account_id',
      label: 'Account',
      type: 'select' as const,
      options: [],
      placeholder: 'All accounts',
    },
    {
      key: 'date_range',
      label: 'Parsed Date',
      type: 'date-range' as const,
    },
  ];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatPeriod = (from: string, to: string) => {
    if (!from || !to) return 'N/A';
    return `${format(new Date(from), 'MMM d')} - ${format(new Date(to), 'MMM d')}`;
  };

  const columns = [
    {
      key: 'customer_name',
      header: 'Customer',
      render: (item: any) => <span className="font-medium">{item.customer_name || 'N/A'}</span>,
    },
    {
      key: 'supplier_name',
      header: 'Supplier',
      render: (item: any) => item.supplier_name || 'N/A',
    },
    {
      key: 'account_number',
      header: 'Account Number',
      render: (item: any) => (
        <span className="font-mono text-sm">{item.account_number || 'N/A'}</span>
      ),
    },
    {
      key: 'invoice_number',
      header: 'Invoice Number',
      render: (item: any) => (
        <span className="font-mono text-sm">{item.parsed_data?.invoice_number || 'N/A'}</span>
      ),
    },
    {
      key: 'invoice_date',
      header: 'Invoice Date',
      render: (item: any) => {
        const date = item.parsed_data?.invoice_date;
        return date ? format(new Date(date), 'MMM d, yyyy') : 'N/A';
      },
    },
    {
      key: 'billing_period',
      header: 'Billing Period',
      render: (item: any) => {
        const from = item.parsed_data?.billing_period_from;
        const to = item.parsed_data?.billing_period_to;
        return formatPeriod(from, to);
      },
    },
    {
      key: 'amount',
      header: 'Amount Due',
      render: (item: any) => {
        const amount = item.parsed_data?.amount_due;
        return amount ? formatCurrency(amount) : 'N/A';
      },
    },
    {
      key: 'due_date',
      header: 'Due Date',
      render: (item: any) => {
        const date = item.parsed_data?.due_date;
        return date ? format(new Date(date), 'MMM d, yyyy') : 'N/A';
      },
    },
    {
      key: 'parsed_date',
      header: 'Parsed Date',
      render: (item: any) => {
        const date = item.parsed_at;
        return date ? format(new Date(date), 'MMM d, yyyy') : 'N/A';
      },
    },
    {
      key: 'files',
      header: 'Files',
      render: (item: any) => (
        <div className="flex gap-2">
          {item.csv_url && (
            <Button variant="ghost" size="sm" asChild>
              <a href={item.csv_url} download>
                <Download className="h-4 w-4" />
              </a>
            </Button>
          )}
          {item.file_url && (
            <Button variant="ghost" size="sm" asChild>
              <a href={item.file_url} target="_blank" rel="noopener noreferrer">
                <Eye className="h-4 w-4" />
              </a>
            </Button>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (item: any) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm">
              Actions
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() =>
                navigate(`/w/${currentWorkspace?.id}/dashboard/bills/${item.id}/details`)
              }
            >
              View Details
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                navigate(`/w/${currentWorkspace?.id}/dashboard/bills/${item.id}/details`)
              }
            >
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem>Approve</DropdownMenuItem>
            <DropdownMenuItem>Reject</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const handleRowClick = (item: any) => {
    navigate(`/w/${currentWorkspace?.id}/dashboard/bills/${item.id}/details`);
  };

  return (
    <ModuleAccessGuard moduleId="bill_processing">
      <div className="container mx-auto py-6 space-y-6">
        <PageHeader
          title="Review Parsed Bills"
          description="View and manage successfully parsed bills"
        />

        <FilterBar
          searchPlaceholder="Search bills..."
          searchValue={search}
          onSearchChange={setSearch}
          filters={filterConfigs}
          activeFilters={activeFilters}
          onFilterChange={(key, value) => setActiveFilters({ ...activeFilters, [key]: value })}
          onClearFilters={() => setActiveFilters({})}
        />

        <DataTable
          data={data?.items || []}
          columns={columns}
          totalCount={data?.total || 0}
          pageSize={pageSize}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          getRowId={(row) => row.id}
          isLoading={isLoading}
          emptyMessage="No parsed bills found"
        />
      </div>
    </ModuleAccessGuard>
  );
};

export default ReviewParsedBills;
